import { EntityModel } from '@/interfaces'
import {
  ALL_TRAVELS_QUERY,
  CREATE_TRAVEL_MUTATION,
  DELETE_TRAVEL_MUTATION,
  FORCE_DELETE_TRAVEL_MUTATION,
  SINGLE_TRAVEL_QUERY,
  UPDATE_TRAVEL_MUTATION,
} from './graphql'
import ratioCatalogBundleEntity from '@/entities/ratioCatalogBundle/entity'
import ratioTravelDateEntity from '@/entities/ratioTravelDate/entity'
import ratioAccommodationEntity from '@/entities/ratioAccommodation/entity'
import ratioExtensionAccommodationEntity from '@/entities/ratioExtensionAccommodation/entity'
import ratioFamilyPackageEntity from '@/entities/ratioFamilyPackage/entity'
import ratioPersonalAddonEntity from '@/entities/ratioPersonalAddon/entity'
import ratioAccommodationAddonEntity from '@/entities/ratioAccommodationAddon/entity'
import ratioBookingAddonEntity from '@/entities/ratioBookingAddon/entity'
import ratioCollectiveAddonEntity from '@/entities/ratioCollectiveAddon/entity'
import ratioTravelExtensionEntity from '@/entities/ratioTravelExtension/entity'
import ratioPersonalDiscountEntity from '@/entities/ratioPersonalDiscount/entity'
import ratioBookingDiscountEntity from '@/entities/ratioBookingDiscount/entity'
import ratioCollectiveDiscountEntity from '@/entities/ratioCollectiveDiscount/entity'

const ratioTravelModel: EntityModel = {
  name: 'travel',
  namePlural: 'travels',
  uniqueName: 'ratioTravel',
  uniqueNamePlural: 'ratioTravels',
  path: 'travel/pricing/adaptors/ratio/travel',
  baseUrl: '/travel/pricing/adaptors/ratio/travels',
  translationPrefix: 'entities:ratioTravel.',
  getDisplayName: (values: any) => {
    return `${values.name} (${values.catalogCode}/${values.travelCode})`
  },
  gql: {
    getAll: ALL_TRAVELS_QUERY,
    getSingle: SINGLE_TRAVEL_QUERY,
    create: CREATE_TRAVEL_MUTATION,
    update: UPDATE_TRAVEL_MUTATION,
    delete: DELETE_TRAVEL_MUTATION,
    forceDelete: FORCE_DELETE_TRAVEL_MUTATION,
  },
  attributes: {
    ratioCatalogBundle: {
      name: 'ratioCatalogBundle',
      label: 'entities:ratioTravel.attributes.ratioCatalogBundle.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return ratioCatalogBundleEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    catalogCode: {
      name: 'catalogCode',
      label: 'entities:ratioTravel.attributes.catalogCode.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    travelCode: {
      name: 'travelCode',
      label: 'entities:ratioTravel.attributes.travelCode.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    destination: {
      name: 'destination',
      label: 'entities:ratioTravel.attributes.destination.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    name: {
      name: 'name',
      label: 'entities:ratioTravel.attributes.name.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    transportType: {
      name: 'transportType',
      label: 'entities:ratioTravel.attributes.transportType.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    transportTypeLabel: {
      name: 'transportTypeLabel',
      label: 'entities:ratioTravel.attributes.transportTypeLabel.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    dates: {
      name: 'dates',
      label: 'entities:ratioTravel.attributes.dates.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioTravelDateEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    accommodations: {
      name: 'accommodations',
      label: 'entities:ratioTravel.attributes.accommodations.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioAccommodationEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    extensionAccommodations: {
      name: 'extensionAccommodations',
      label: 'entities:ratioTravel.attributes.extensionAccommodations.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioExtensionAccommodationEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    familyPackages: {
      name: 'familyPackages',
      label: 'entities:ratioTravel.attributes.familyPackages.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioFamilyPackageEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    personalAddons: {
      name: 'personalAddons',
      label: 'entities:ratioTravel.attributes.personalAddons.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioPersonalAddonEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    accommodationAddons: {
      name: 'accommodationAddons',
      label: 'entities:ratioTravel.attributes.accommodationAddons.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioAccommodationAddonEntity
      },
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    bookingAddons: {
      name: 'bookingAddons',
      label: 'entities:ratioTravel.attributes.bookingAddons.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioBookingAddonEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    collectiveAddons: {
      name: 'collectiveAddons',
      label: 'entities:ratioTravel.attributes.collectiveAddons.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioCollectiveAddonEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    extensions: {
      name: 'extensions',
      label: 'entities:ratioTravel.attributes.extensions.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioTravelExtensionEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    personalDiscounts: {
      name: 'personalDiscounts',
      label: 'entities:ratioTravel.attributes.personalDiscounts.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioPersonalDiscountEntity
      },
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    bookingDiscounts: {
      name: 'bookingDiscounts',
      label: 'entities:ratioTravel.attributes.bookingDiscounts.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioBookingDiscountEntity
      },
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    collectiveDiscounts: {
      name: 'collectiveDiscounts',
      label: 'entities:ratioTravel.attributes.collectiveDiscounts.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioCollectiveDiscountEntity
      },
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    importDateTime: {
      name: 'importDateTime',
      label: 'entities:ratioTravel.attributes.importDateTime.label',
      type: 'value',
      dataType: 'string',
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    importStatus: {
      name: 'importStatus',
      label: 'entities:ratioTravel.attributes.importStatus.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    suppressBusBoardingSelection: {
      name: 'suppressBusBoardingSelection',
      label: 'entities:ratioTravel.attributes.suppressBusBoardingSelection.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
  },
}

export default ratioTravelModel
